import React, { useState, useEffect } from 'react';
import { 
  Lock, 
  LogOut, 
  Users, 
  Mail, 
  Phone, 
  Calendar,
  DollarSign,
  MessageSquare,
  RefreshCw,
  Search,
  ChevronDown,
  ChevronUp,
  ExternalLink
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Simple password - you should change this!
const ADMIN_PASSWORD = 'BaliLife2024!';

const AdminLeads = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [expandedContact, setExpandedContact] = useState(null);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setPasswordError(false);
      localStorage.setItem('adminAuth', 'true');
      fetchContacts();
    } else {
      setPasswordError(true);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('adminAuth');
  };

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/contacts`, {
        headers: {
          'X-Admin-Key': ADMIN_PASSWORD
        }
      });
      if (response.ok) {
        const data = await response.json();
        setContacts(data.contacts || []);
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const auth = localStorage.getItem('adminAuth');
    if (auth === 'true') {
      setIsAuthenticated(true);
      fetchContacts();
    }
  }, []);

  const filteredContacts = contacts
    .filter(contact => {
      const search = searchTerm.toLowerCase();
      return (
        contact.name?.toLowerCase().includes(search) ||
        contact.email?.toLowerCase().includes(search) ||
        contact.phone?.toLowerCase().includes(search)
      );
    })
    .sort((a, b) => {
      const aVal = a[sortField] || '';
      const bVal = b[sortField] || '';
      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#1A1A2E] flex items-center justify-center p-4">
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#C9A227] to-[#B8922B] flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white">Admin - Leads</h1>
            <p className="text-white/60 text-sm mt-2">Bali Life Concierge</p>
          </div>

          <form onSubmit={handleLogin}>
            <div className="mb-6">
              <label className="block text-white/80 text-sm mb-2">Mot de passe</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`w-full px-4 py-3 bg-white/10 border rounded-xl text-white placeholder-white/40 focus:outline-none transition-colors ${
                  passwordError ? 'border-red-500' : 'border-white/20 focus:border-[#C9A227]'
                }`}
                placeholder="Entrez le mot de passe"
              />
              {passwordError && (
                <p className="text-red-400 text-sm mt-2">Mot de passe incorrect</p>
              )}
            </div>

            <button
              type="submit"
              className="w-full bg-[#C9A227] hover:bg-[#B8922B] text-white font-semibold py-3 rounded-xl transition-colors"
            >
              Connexion
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-[#0F0F1A]">
      {/* Header */}
      <header className="bg-[#1A1A2E] border-b border-white/10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#C9A227] to-[#B8922B] flex items-center justify-center">
              <span className="text-white font-bold">BL</span>
            </div>
            <div>
              <h1 className="text-white font-semibold">Admin - Leads</h1>
              <p className="text-white/50 text-xs">Bali Life Concierge</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
          >
            <LogOut className="w-5 h-5" />
            Déconnexion
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-[#1A1A2E] rounded-xl p-6 border border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-[#C9A227]/20 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-[#C9A227]" />
              </div>
              <div>
                <p className="text-white/50 text-sm">Total Leads</p>
                <p className="text-2xl font-bold text-white">{contacts.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-[#1A1A2E] rounded-xl p-6 border border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                <Calendar className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <p className="text-white/50 text-sm">Aujourd'hui</p>
                <p className="text-2xl font-bold text-white">
                  {contacts.filter(c => {
                    const today = new Date().toDateString();
                    const contactDate = new Date(c.created_at).toDateString();
                    return today === contactDate;
                  }).length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-[#1A1A2E] rounded-xl p-6 border border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <p className="text-white/50 text-sm">Budget 300k+</p>
                <p className="text-2xl font-bold text-white">
                  {contacts.filter(c => c.budget === '300k+').length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-[#1A1A2E] rounded-xl p-6 border border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                <MessageSquare className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <p className="text-white/50 text-sm">Instagram</p>
                <p className="text-2xl font-bold text-white">
                  {contacts.filter(c => c.source?.includes('Instagram')).length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Search & Refresh */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
            <input
              type="text"
              placeholder="Rechercher par nom, email ou téléphone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-[#1A1A2E] border border-white/10 rounded-xl text-white placeholder-white/40 focus:border-[#C9A227] focus:outline-none"
            />
          </div>
          <button
            onClick={fetchContacts}
            disabled={loading}
            className="flex items-center gap-2 px-6 py-3 bg-[#C9A227] hover:bg-[#B8922B] text-white rounded-xl transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </button>
        </div>

        {/* Contacts Table */}
        <div className="bg-[#1A1A2E] rounded-xl border border-white/10 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th 
                    className="text-left px-6 py-4 text-white/60 font-medium cursor-pointer hover:text-white"
                    onClick={() => toggleSort('created_at')}
                  >
                    <div className="flex items-center gap-2">
                      Date
                      {sortField === 'created_at' && (
                        sortDirection === 'desc' ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />
                      )}
                    </div>
                  </th>
                  <th 
                    className="text-left px-6 py-4 text-white/60 font-medium cursor-pointer hover:text-white"
                    onClick={() => toggleSort('name')}
                  >
                    <div className="flex items-center gap-2">
                      Nom
                      {sortField === 'name' && (
                        sortDirection === 'desc' ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />
                      )}
                    </div>
                  </th>
                  <th className="text-left px-6 py-4 text-white/60 font-medium">Contact</th>
                  <th className="text-left px-6 py-4 text-white/60 font-medium">Budget</th>
                  <th className="text-left px-6 py-4 text-white/60 font-medium">Source</th>
                  <th className="text-left px-6 py-4 text-white/60 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredContacts.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-white/40">
                      {loading ? 'Chargement...' : 'Aucun lead trouvé'}
                    </td>
                  </tr>
                ) : (
                  filteredContacts.map((contact, index) => (
                    <React.Fragment key={contact.id || index}>
                      <tr 
                        className="border-b border-white/5 hover:bg-white/5 cursor-pointer"
                        onClick={() => setExpandedContact(expandedContact === index ? null : index)}
                      >
                        <td className="px-6 py-4">
                          <span className="text-white/80 text-sm">{formatDate(contact.created_at)}</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-white font-medium">{contact.name}</span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col gap-1">
                            <a href={`mailto:${contact.email}`} className="text-[#C9A227] hover:underline text-sm flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {contact.email}
                            </a>
                            {contact.phone && (
                              <a href={`tel:${contact.phone}`} className="text-white/60 hover:text-white text-sm flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {contact.phone}
                              </a>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {contact.budget ? (
                            <span className="bg-[#C9A227]/20 text-[#C9A227] px-3 py-1 rounded-full text-xs font-medium">
                              {contact.budget}
                            </span>
                          ) : (
                            <span className="text-white/40 text-sm">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-white/60 text-sm">{contact.source || 'Direct'}</span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <a 
                              href={`https://wa.me/${contact.phone?.replace(/[^0-9]/g, '')}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 bg-emerald-500/20 text-emerald-400 rounded-lg hover:bg-emerald-500/30 transition-colors"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          </div>
                        </td>
                      </tr>
                      {expandedContact === index && contact.message && (
                        <tr className="bg-white/5">
                          <td colSpan="6" className="px-6 py-4">
                            <div className="flex items-start gap-2">
                              <MessageSquare className="w-4 h-4 text-white/40 mt-1" />
                              <div>
                                <p className="text-white/40 text-xs mb-1">Message:</p>
                                <p className="text-white/80 text-sm">{contact.message}</p>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminLeads;
